/**
 * Les lectures de la règle « un stage par semaine et par enfant »
 * (`semaines.ts`) : les inscriptions des enfants AVEC les dates de leur
 * session, et les sessions du panier ou des demandes dont la page ne connaît
 * que l'identifiant.
 *
 * Les inscriptions sont filtrées sur les enfants du compte, pas laissées à la
 * RLS : un compte de l'équipe qui a aussi des enfants lit toutes les
 * inscriptions de l'ASBL (`staff_select_registrations`), et la page lui
 * dirait que la semaine de son propre enfant est prise par celui d'un autre.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import type { InscriptionDatee, SessionRelue } from './semaines';

const sansTypes = () => supabase as unknown as SupabaseClient;

/** Une jointure arrive en objet, ou en tableau selon la relation : on prend le premier. */
const un = <T>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);

interface LigneSession {
  id: string;
  start_date: string;
  end_date: string;
  active: boolean | null;
  archived: boolean | null;
  visible_from: string | null;
  capacity: number;
  current_registrations: number | null;
  stage: { title: string; type_activite: string | null } | { title: string; type_activite: string | null }[] | null;
  center: { name: string } | { name: string }[] | null;
}

const COLONNES_SESSION = `
  id, start_date, end_date, active, archived, visible_from, capacity, current_registrations,
  stage:stages(title, type_activite),
  center:centers(name)
`;

const versSession = (s: LigneSession): SessionRelue => ({
  id: s.id,
  start_date: s.start_date,
  end_date: s.end_date,
  active: s.active,
  archived: s.archived,
  visible_from: s.visible_from,
  capacity: s.capacity,
  current_registrations: s.current_registrations,
  titre: un(s.stage)?.title ?? 'Stage',
  type_activite: un(s.stage)?.type_activite ?? null,
  centre: un(s.center)?.name ?? null,
});

/** Les sessions demandées, par identifiant. Rien à demander : rien à lire. */
export async function lireSessions(ids: readonly string[]): Promise<Map<string, SessionRelue>> {
  const uniques = [...new Set(ids)];
  if (uniques.length === 0) return new Map();
  const { data, error } = await sansTypes().from('sessions').select(COLONNES_SESSION).in('id', uniques);
  if (error) throw error;
  return new Map(((data ?? []) as LigneSession[]).map((s) => [s.id, versSession(s)]));
}

/**
 * Les inscriptions de ces enfants, avec les dates de leur session — y compris
 * les annulées : c'est `occupantsDesInscriptions` qui trie, une règle à un seul
 * endroit.
 */
export async function lireInscriptionsDatees(kidIds: readonly string[]): Promise<InscriptionDatee[]> {
  if (kidIds.length === 0) return [];
  const { data, error } = await sansTypes()
    .from('registrations')
    .select(`id, kid_id, activity_id, payment_status, cancellation_status, session:activity_id(${COLONNES_SESSION})`)
    .in('kid_id', [...kidIds]);
  if (error) throw error;

  type Ligne = Omit<InscriptionDatee, 'session'> & { session: LigneSession | LigneSession[] | null };
  return ((data ?? []) as Ligne[]).map((r) => {
    const s = un(r.session);
    return { ...r, session: s ? versSession(s) : null };
  });
}
